// KVKK DSAR (İlgili Kişi Başvurusu) contracts — the admin console's view of the
// source-verified /v1/admin/dsar API (apps/api/src/modules/dsar). Types ONLY; the API
// is the boundary and re-enforces every grant. Grounded 1:1 on the controller +
// DsarRegisterService + @csa/validation (packages/validation/src/dsar.ts) + the
// compliance.dsar_request table ($inferSelect):
//   - POST  /v1/admin/dsar      → DsarRequest (created; due_at = received_at + 30d)  dsar:write@all
//   - GET   /v1/admin/dsar      → Page<DsarRequest> (?status ?type; keyset cursor)   dsar:read@all
//   - PATCH /v1/admin/dsar/:id  → DsarRequest (forward-legal status transition)      dsar:write@all
//   - POST  /v1/admin/dsar/:id/execute-erasure → DsarRequest & {erasure}             dsar:write@all
//         + four-eyes (dsr.erase, bound to the DSAR id) + step-up. ERASURE-only.
//
// Enums are duplicated (not imported) to keep the admin app free of a zod/validation
// dependency — matching the "types only" convention of the other *-contracts modules.

/** KVKK m.11 subject-right request types (mirrors DSAR_REQUEST_TYPES). */
export const DSAR_REQUEST_TYPES = [
  "ACCESS",
  "ERASURE",
  "RECTIFICATION",
  "OBJECTION",
  "PORTABILITY",
] as const;
export type DsarRequestType = (typeof DSAR_REQUEST_TYPES)[number];

/** Intake channels (mirrors DSAR_CHANNELS). */
export const DSAR_CHANNELS = ["EMAIL", "PHONE", "POST", "IN_PERSON", "WEB"] as const;
export type DsarChannel = (typeof DSAR_CHANNELS)[number];

/** Handling statuses (mirrors compliance.DSAR_STATUSES). RECEIVED is the create-only
 *  initial state; the PATCH may only set a non-initial state. */
export const DSAR_STATUSES = ["RECEIVED", "IN_PROGRESS", "FULFILLED", "REJECTED"] as const;
export type DsarStatus = (typeof DSAR_STATUSES)[number];

/** The forward-legal transition map, mirrored from DsarRegisterService.updateStatus.
 *  Terminal states (FULFILLED / REJECTED) are final. The service re-enforces regardless;
 *  the surface only offers the legal moves as UX. */
export const DSAR_TRANSITIONS: Record<DsarStatus, readonly DsarStatus[]> = {
  RECEIVED: ["IN_PROGRESS", "REJECTED"],
  IN_PROGRESS: ["FULFILLED", "REJECTED"],
  FULFILLED: [],
  REJECTED: [],
};

/** The four-eyes action bound to the KVKK crypto-shred (APPROVAL_POLICY["dsr.erase"]).
 *  The approval is resource-bound to the DSAR request id on this path. */
export const DSR_ERASE_ACTION = "dsr.erase";

/**
 * The transitions an operator may drive by HAND (PATCH /:id). For an ERASURE
 * request FULFILLED is deliberately EXCLUDED: marking a KVKK erasure "fulfilled"
 * must go through POST /:id/execute-erasure, which actually runs the crypto-shred
 * and stamps `erasureShredAuditId`. Before this guard existed an operator could
 * close an erasure while nothing was destroyed — a compliance record asserting a
 * deletion that never happened.
 */
export function manualTransitions(row: {
  status: DsarStatus;
  requestType: DsarRequestType;
}): readonly DsarStatus[] {
  const moves = DSAR_TRANSITIONS[row.status];
  if (row.requestType !== "ERASURE") return moves;
  return moves.filter((s) => s !== "FULFILLED");
}

/** The outcome of the S3-1 category-aware crypto-shred (DsrService.EraseResult),
 *  echoed on the execute-erasure response. Counts, plus the correlation id stamped
 *  on the shredded keys and the audit trail — the durable erasure receipt. */
export interface EraseResult {
  subjectType: string;
  subjectId: string;
  /** Non-floor keys crypto-shredded (DEK destroyed). */
  shredded: number;
  /** Floor keys locked — key retained until retention_until (commercial record). */
  restricted: number;
  /** Keys already non-ACTIVE (idempotent no-op). */
  skipped: number;
  /** Correlation id for this erase — the reference stored on the DSAR row. */
  shredAuditId: string;
}

/** POST /v1/admin/dsar/:id/execute-erasure → the updated DSAR + the erasure receipt. */
export type ExecuteErasureResult = DsarRequest & { erasure: EraseResult };

/** The DSAR register row (GET list + POST/PATCH result). Timestamps are ISO strings
 *  over JSON. `fulfilledAt` is stamped only when a terminal status is set. */
export interface DsarRequest {
  id: string;
  organizationId: string;
  subjectPartyId: string;
  /** The data subject's resolved display NAME (party.party first+last), joined
   *  server-side under the same org RLS wall. The compliance desk identifies the
   *  İlgili Kişi without a raw uuid. Null-safe: an unresolved subject → null and the
   *  surface falls back to `subjectPartyId`. Present on the list read; the
   *  create/patch echo omits it (undefined → treated as the null fallback). The name
   *  is plaintext-under-RLS — NOT the masked-TCKN case. */
  subjectName?: string | null;
  requestType: DsarRequestType;
  status: DsarStatus;
  channel: DsarChannel;
  receivedAt: string;
  /** KVKK m.13 statutory deadline — received_at + 30 days (server-derived). */
  dueAt: string;
  fulfilledAt: string | null;
  /** Set ONLY by execute-erasure — the correlation id of the crypto-shred that
   *  actually destroyed the subject's keys (compliance.dsar_request.
   *  erasure_shred_audit_id, migration 0056). Its presence is the difference
   *  between "we recorded a deletion" and "we performed one". */
  erasureShredAuditId?: string | null;
  note: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

/** POST body — log a new DSAR. `subjectPartyId` is a party uuid; `due_at` is
 *  server-derived and never client-supplied. `note` is optional. */
export interface CreateDsarInput {
  subjectPartyId: string;
  requestType: DsarRequestType;
  channel: DsarChannel;
  note?: string;
}

/** PATCH body — advance the handling status (a forward-legal transition only). */
export interface UpdateDsarStatusInput {
  status: DsarStatus;
  note?: string;
}

/** Optional server-side list filters (?status ?type). */
export interface DsarFilter {
  status?: DsarStatus;
  requestType?: DsarRequestType;
}

/** A DSAR is overdue when its statutory deadline has passed and it is not yet in a
 *  terminal state (FULFILLED / REJECTED) — a calm "gecikmiş" hint, never an alarm. */
export function isOverdue(row: DsarRequest, now: number = Date.now()): boolean {
  if (row.status === "FULFILLED" || row.status === "REJECTED") return false;
  return new Date(row.dueAt).getTime() < now;
}
